import type { GeoTile, ApiMeta, ChargepointSummary } from '@fuelfinder/shared'

/**
 * Base URL for API requests
 * Uses relative path that works with Vite dev proxy and production
 */
const API_BASE = '/api'

/**
 * Fetch a single tile by geohash
 */
export async function fetchTile(geohash: string): Promise<GeoTile> {
  const response = await fetch(`${API_BASE}/tiles/${geohash}.json`)
  if (!response.ok) {
    // Return empty tile for 404s (tile doesn't exist for this geohash)
    if (response.status === 404) {
      return { geohash, chargepoints: [] }
    }
    throw new Error(`Failed to fetch tile ${geohash}: ${response.status}`)
  }

  // Check Content-Type to detect SPA fallback (CloudFront returns index.html for missing tiles)
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    return { geohash, chargepoints: [] }
  }

  const data = await response.json()

  // Handle old format (stations) or missing chargepoints gracefully
  const chargepoints = data.chargepoints ?? data.stations ?? []
  return { geohash, chargepoints: Array.isArray(chargepoints) ? chargepoints : [] }
}

/**
 * Fetch the full national dataset (slim summaries) for zoomed-out views.
 * ~1.1MB gzipped over the wire; callers should fetch it at most once.
 */
export async function fetchNationalDataset(): Promise<ChargepointSummary[]> {
  const response = await fetch(`${API_BASE}/all.json`)
  if (!response.ok) {
    throw new Error(`Failed to fetch all.json: ${response.status}`)
  }
  const contentType = response.headers.get('content-type') || ''
  if (!contentType.includes('application/json')) {
    return []
  }
  const data = await response.json()
  return Array.isArray(data) ? data : []
}

/**
 * Fetch API metadata (last updated, provider status)
 */
export async function fetchMeta(): Promise<ApiMeta> {
  const response = await fetch(`${API_BASE}/meta.json`)
  if (!response.ok) {
    throw new Error(`Failed to fetch meta: ${response.status}`)
  }
  return response.json()
}

/**
 * Fetch multiple tiles in parallel and merge/dedupe chargepoints
 * @param geohashes Array of geohash strings to fetch
 * @returns Merged array of unique chargepoint summaries
 */
export async function fetchAllTiles(geohashes: string[]): Promise<ChargepointSummary[]> {
  const tiles = await Promise.all(geohashes.map(fetchTile))

  // Merge and dedupe chargepoints by id
  const cpMap = new Map<string, ChargepointSummary>()
  for (const tile of tiles) {
    for (const cp of tile.chargepoints) {
      if (!cpMap.has(cp.id)) {
        cpMap.set(cp.id, cp)
      }
    }
  }

  return Array.from(cpMap.values())
}
